// filepath: apps/web/src/features/cabinet/usePathname.ts
//
// FR-UI-CABINET-1 AC-1 / AC-9 — shared client-side navigation
// primitive for the tenant workspace shell.
//
// The tenant SPA does NOT use `react-router-dom` (AC-9 — App.tsx is
// materially more entangled than the operator surface and a router
// migration is explicitly out of scope). Instead it has an
// established hand-rolled `window.history.pushState` +
// `window.dispatchEvent(new PopStateEvent("popstate"))` nav
// convention, already used by:
//   - `apps/web/src/features/proc/ProcRoute.tsx` (defaultPushTrace /
//     defaultBackToDashboard), and
//   - `apps/web/src/features/observability/DrilldownLink.tsx`
//     (onClick pushState + popstate + preventDefault).
//
// This module extracts that convention into a shared primitive so
// the shell's sidebar items and `AuthStateRouter` reuse ONE
// implementation:
//   - `usePathname()` — a `useSyncExternalStore` hook over the
//     `popstate` event, mirroring the precedent in
//     `apps/web/src/operator/useOperatorToken.ts` (the operator
//     token store uses the same `useSyncExternalStore` pattern).
//     Replaces `AuthStateRouter`'s direct `window.location.pathname`
//     read so the shell re-renders on every sidebar section switch.
//   - `navigate(path)` — the pushState + popstate dispatcher used by
//     sidebar items (and reusable by any tenant surface). Identical
//     to `DrilldownLink`'s onClick path.
//
// AC-1 ("no full page reload on section switch"): `navigate` uses
// ONLY `history.pushState` + a synthetic `popstate` event — it NEVER
// calls `window.location.assign` / `.replace` / `.reload`. The
// TestDesigner AC-1 test asserts `window.location.assign` /
// `.reload` are not invoked on a sidebar section switch.
//
// AC-9 (router scope lock): this module imports NOTHING from
// `react-router-dom`. The static-guard test (TestDesigner, mirroring
// the FR-UI-CORE-5 pattern) asserts zero `react-router-dom` imports
// across `App.tsx` + `apps/web/src/features/cabinet/**`.
//
// SECURITY: no tokens, no tenant identifiers, no secrets flow through
// this module. The pathname is a client-side routing signal only; the
// server resolves the tenant from the OIDC realm + hostname (INV-1).

import { useSyncExternalStore } from "react";

/**
 * Subscribe to URL changes. `useSyncExternalStore` calls this once
 * per consumer; we attach a single `popstate` listener and tear it
 * down on unsubscribe. `pushState` itself fires NO event, so
 * `navigate()` below manually dispatches a synthetic `popstate` to
 * notify subscribers — the same trick `ProcRoute` / `DrilldownLink`
 * already use.
 */
function subscribe(callback: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener("popstate", callback);
  return () => {
    window.removeEventListener("popstate", callback);
  };
}

/**
 * Client snapshot — the current pathname. `useSyncExternalStore`
 * compares snapshots with `Object.is`; strings compare by value, so
 * returning `window.location.pathname` directly is referentially
 * stable across renders (React only re-renders when the VALUE
 * changes).
 */
function getSnapshot(): string {
  return typeof window !== "undefined" ? window.location.pathname : "/";
}

/**
 * Server snapshot (SSR / no-window). The SPA is client-only, but
 * `useSyncExternalStore` mandates a server snapshot to avoid
 * hydration warnings.
 */
function getServerSnapshot(): string {
  return "/";
}

/**
 * Reactive pathname hook. Re-renders the calling component whenever
 * the URL changes (via `popstate` — including the synthetic events
 * `navigate()` dispatches). Drop-in replacement for the direct
 * `window.location.pathname` read that `AuthStateRouter` previously
 * performed on every render.
 */
export function usePathname(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Navigate to `path` WITHOUT a full page reload (AC-1). Pushes a new
 * history entry and dispatches a synthetic `popstate` so every
 * `usePathname()` consumer re-renders. Mirrors `ProcRoute`'s
 * `defaultPushTrace` / `DrilldownLink`'s onClick exactly.
 *
 * This is the single nav primitive the sidebar items call; it never
 * touches `window.location.assign` / `.replace` / `.reload`.
 */
export function navigate(path: string): void {
  if (typeof window === "undefined") return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
